import { Injectable, signal } from '@angular/core';
import { MenuItem } from 'primeng/api';

@Injectable({ providedIn: 'root' })
export class AppNavigationService {
  readonly menuItems = signal<MenuItem[]>([]);
  readonly homeResetTrigger = signal<number>(0);

  setMenuItems(items: MenuItem[]): void {
    this.menuItems.set(items);
  }

  triggerHomeReset(): void {
    this.homeResetTrigger.update(count => count + 1);
  }

  resolveBreadcrumbs(url: string): MenuItem[] {
    const normalizedUrl = this.normalizeUrl(url);
    const trail = this.findBestTrail(this.menuItems(), normalizedUrl);

    if (!trail.length) {
      return [];
    }

    const matchedRoute = this.normalizeRouterLink(trail[trail.length - 1]?.routerLink);
    const derivedLabel = this.deriveTailLabel(normalizedUrl, matchedRoute);
    const hasDerivedTail = !!derivedLabel;

    const breadcrumbs = trail.map((item, index) => {
      const routerLink = this.resolveMenuItemRoute(item);
      const isLastVisibleCrumb = !hasDerivedTail && index === trail.length - 1;

      return {
        label: item.label,
        ...(!isLastVisibleCrumb && routerLink ? { routerLink } : {})
      } satisfies MenuItem;
    });

    if (derivedLabel && breadcrumbs[breadcrumbs.length - 1]?.label !== derivedLabel) {
      breadcrumbs.push({ label: derivedLabel });
    }

    return breadcrumbs;
  }

  private findBestTrail(items: MenuItem[], url: string, trail: MenuItem[] = []): MenuItem[] {
    let bestTrail: MenuItem[] = [];
    let bestScore = -1;

    for (const item of items) {
      if (item.visible === false) {
        continue;
      }

      const nextTrail = [...trail, item];
      const score = this.routeMatchScore(item, url);
      if (score > bestScore) {
        bestScore = score;
        bestTrail = nextTrail;
      }

      const childTrail = this.findBestTrail(this.getChildItems(item), url, nextTrail);
      const childScore = this.trailScore(childTrail, url);
      if (childScore > bestScore) {
        bestScore = childScore;
        bestTrail = childTrail;
      }
    }

    return bestTrail;
  }

  private trailScore(trail: MenuItem[], url: string): number {
    if (!trail.length) {
      return -1;
    }

    return this.routeMatchScore(trail[trail.length - 1], url);
  }

  private routeMatchScore(item: MenuItem, currentUrl: string): number {
    const routerLink = this.normalizeRouterLink(item.routerLink);
    if (!routerLink) {
      return -1;
    }

    const targetUrl = this.normalizeUrl(routerLink);
    if (currentUrl === targetUrl) {
      return 1000 + targetUrl.length;
    }

    if (currentUrl.startsWith(`${targetUrl}/`)) {
      return 500 + targetUrl.length;
    }

    const currentSegments = currentUrl.split('/').filter(Boolean);
    const targetSegments = targetUrl.split('/').filter(Boolean);
    let sharedSegments = 0;

    while (
      sharedSegments < currentSegments.length &&
      sharedSegments < targetSegments.length &&
      currentSegments[sharedSegments] === targetSegments[sharedSegments]
    ) {
      sharedSegments += 1;
    }

    return sharedSegments > 0 ? sharedSegments * 10 : -1;
  }

  private deriveTailLabel(currentUrl: string, matchedRoute: string | null): string | null {
    const currentSegments = currentUrl.split('/').filter(Boolean);
    const matchedSegments = (matchedRoute ?? '').split('/').filter(Boolean);
    const remainingSegments = currentSegments.slice(matchedSegments.length).filter(segment => !this.isRouteId(segment));

    if (!remainingSegments.length) {
      return null;
    }

    return this.humanizeSegment(remainingSegments[0]);
  }

  private humanizeSegment(segment: string): string {
    return segment
      .replace(/[-_]+/g, ' ')
      .replace(/\b\w/g, character => character.toUpperCase());
  }

  private isRouteId(segment: string): boolean {
    return /^\d+$/.test(segment)
      || /^[0-9a-f]{8,}$/i.test(segment)
      || /^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(segment);
  }

  private getChildItems(item: MenuItem): MenuItem[] {
    return Array.isArray(item.items) ? item.items : [];
  }

  private resolveMenuItemRoute(item: MenuItem): string | null {
    const directRoute = this.normalizeRouterLink(item.routerLink);
    if (directRoute) {
      return directRoute;
    }

    for (const child of this.getChildItems(item)) {
      const childRoute = this.resolveMenuItemRoute(child);
      if (childRoute) {
        return childRoute;
      }
    }

    return null;
  }

  private normalizeRouterLink(routerLink: MenuItem['routerLink']): string | null {
    if (Array.isArray(routerLink)) {
      const resolved = routerLink
        .map(segment => `${segment ?? ''}`.trim())
        .filter(Boolean)
        .join('/');

      return resolved ? this.normalizeUrl(resolved.startsWith('/') ? resolved : `/${resolved}`) : null;
    }

    if (typeof routerLink === 'string' && routerLink.trim()) {
      return this.normalizeUrl(routerLink);
    }

    return null;
  }

  private normalizeUrl(url: string): string {
    return url.split('?')[0].replace(/\/+$/, '') || '/';
  }
}
